import { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { add, format, parse, zero, type Money as MoneyValue } from '@fint/money';
import { Briefcase, LayoutGrid } from 'lucide-react';
import { api, qs } from '@/lib/api';
import { useAuthStore, useActiveOrg } from '@/stores/auth';
import { useSwitchOrg } from '@/hooks/useSwitchOrg';
import { PageHeader } from '@/app/DashboardLayout';
import { financialYearOf, financialYearPeriod, toApiDate, type Period } from '@/lib/dates';
import { useFiscalYearStartMonth } from '@/hooks/useOrgSettings';
import {
  Card, CardContent, CardHeader, CardTitle, ColourDot, EmptyState, ErrorState, GstChip, Money, PeriodPicker,
  RankedBars, SkeletonTable, TBody, TD, TH, THead, TR, Table,
} from '@/features/common/ui';
import { BasisToggle, Signed, Tile, describeBasis, type Basis } from '@/features/dashboard/shared';
import {
  hasFigures,
  magnitudeShares,
  profitColour,
  profitLabel,
  rowsInTotal,
  type OverviewOrgRow,
  type OverviewResponse,
} from './overview-rows';
import { OverviewCharts } from './OverviewCharts';
import { OverviewMonths } from './OverviewMonths';

/**
 * Every business this person belongs to, on one screen, for one period.
 *
 * ── WHAT IT IS THE SIBLING OF ──
 * "Money in and out" on the per-org dashboard: the same three tiles, the same
 * basis toggle, the same period picker, the same signed profit. This page is
 * that section with the businesses as rows instead of the months. It shares
 * the components rather than the look, so the two cannot drift apart.
 *
 * ── WHY IT IS NOT THE CLIENTS PAGE, AND WHY CLIENTS STAYS ──
 * Clients answers "which of these needs attention before filing": period
 * locks, open queries, the review engine's findings, the GSTIN. It is the
 * practising accountant's work queue and it can run fourteen checks across
 * forty books on request, which is not something a page that opens on every
 * sign-in should offer a button for. This page answers "how are they doing":
 * income, expense, profit, what they are owed. Same rows, different question,
 * and folding the second into the first would have put a P&L beside a filing
 * status and made both harder to read. So: Overview is where a person with
 * more than one business lands, and Clients is a link away for the filing
 * view. A single-org user gets neither - the Overview redirects to the
 * dashboard, and the nav never offers it.
 *
 * ── AND THEN THE CHARTS ──
 * Under the tiles, before the table: the months as stacked columns, the
 * running profit as a line, the categories as two rings, and cash and
 * receivables as bars per book. They come from their own endpoint
 * (`OverviewCharts`), follow the same period and basis, and can fail on
 * their own without taking the table with them.
 *
 * ── WHAT IT WILL NOT DO WITH MONEY ──
 * The all-business totals arrive from the API, which decided which rows were
 * summable. The two column totals it does not send - receivables and overdue
 * - are added here through `@fint/money`, over exactly the rows the API would
 * have counted. Nothing on this page passes an amount through `Number()`.
 */
export function OverviewPage() {
  const allOrgs = useAuthStore((s) => s.orgs);
  const organisations = useAuthStore((s) => s.organisations);
  const [searchParams] = useSearchParams();
  const activeBook = useActiveOrg();
  /**
   * ── ONE ORGANISATION AT A TIME ──
   * With more than one organisation the Overview is always one of them
   * (0022): `?organisation=` names it, and without it the active book's
   * organisation is the one meant. A demo shelf's figures never sit in the
   * same total as the real ones, and the way to see another organisation's
   * books is to switch to it - the control above the book switcher. One
   * organisation, and every book is added up, as it always was.
   */
  const scopeId = searchParams.get('organisation');
  const several = organisations.length > 1;
  const booksIn = (organisationId: string) => allOrgs.filter((o) => o.organisationId === organisationId);
  const named = several ? organisations.find((o) => o.id === scopeId) : undefined;
  const active = several ? organisations.find((o) => o.id === activeBook?.organisationId) : undefined;
  // The address names it; else the active book's, if there is anything to
  // add up there; else the first organisation where there is - the person
  // signing in with one book of their own and two of a client's lands on
  // the client's, which is the only comparison they have.
  const organisation = several
    ? (named ?? (active && booksIn(active.id).length > 1 ? active : organisations.find((o) => booksIn(o.id).length > 1)))
    : undefined;
  const orgs = organisation ? booksIn(organisation.id) : allOrgs;
  const switchTo = useSwitchOrg();
  /**
   * ── THE FINANCIAL YEAR, NOT THE MONTH ──
   * The dashboard opens on this month because that is the question a
   * single book is asked most. The Overview is asked "how is the year
   * going, across everything" - and a month of it is one point on the
   * running-profit line and one row in the table, which read as a chart
   * that failed. The active book's FY start decides the year (April for
   * an Indian book; 4 until settings arrive, which is the same answer).
   * The chips and the select still take it anywhere in one tap.
   */
  const fyStart = useFiscalYearStartMonth();
  const [period, setPeriod] = useState<Period>(() =>
    financialYearPeriod(financialYearOf(toApiDate(new Date()), fyStart).startYear, fyStart),
  );
  // "When paid" first, as the Dashboard opens - see MoneyInAndOut.tsx.
  const [basis, setBasis] = useState<Basis>('cash');

  const query = useQuery({
    // No org id in the key: this is the one query keyed to the person rather
    // than the tenant. The org switch clears the cache anyway.
    queryKey: ['practice-overview', organisation?.id ?? null, period.from, period.to, basis],
    queryFn: () =>
      api.get<OverviewResponse>(`/practice/overview${qs({ from: period.from, to: period.to, basis, organisationId: organisation?.id })}`),
    enabled: orgs.length > 1,
  });

  // The persisted membership list is a hint and the nav already hides this
  // page when there is nothing to add up; the route still has to refuse
  // rather than render a one-row comparison for somebody who typed the
  // address. The address always names the organisation it shows.
  if (orgs.length < 2) return <Navigate to="/" replace />;
  if (organisation && scopeId !== organisation.id) return <Navigate to={`/overview?organisation=${organisation.id}`} replace />;

  const data = query.data;
  const currency = data?.currency ?? 'INR';
  const rows = data?.orgs ?? [];
  const counted = rowsInTotal(rows, currency);

  return (
    <>
      <PageHeader
        title={organisation ? `Overview · ${organisation.name}` : 'Overview'}
        subtitle={organisation ? `${organisation.name}'s books, side by side. Open one to see its books.` : 'Every business you belong to, side by side. Open one to see its books.'}
      />

      <div className="space-y-4 px-5 pb-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <p className="max-w-xl text-sm text-muted-foreground">{describeBasis(basis)}</p>
          <div className="flex flex-wrap items-center gap-3">
            <BasisToggle value={basis} onChange={setBasis} />
            <PeriodPicker value={period} onChange={setPeriod} id="overview-period" />
          </div>
        </div>

        {query.isError ? (
          <Card>
            <ErrorState error={query.error} onRetry={() => { void query.refetch(); }} />
          </Card>
        ) : query.isPending ? (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3" aria-busy="true">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="skeleton h-24 rounded-lg" />
              ))}
            </div>
            <Card>
              <SkeletonTable rows={orgs.length} cols={7} />
            </Card>
          </>
        ) : data && rows.length === 0 ? (
          <Card>
            <EmptyState
              icon={Briefcase}
              title="No businesses to show"
              description="Every business you are a member of appears here once it has been set up."
            />
          </Card>
        ) : data ? (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
              <Tile
                label="Money in, all businesses"
                value={<Money value={data.totals.income} currency={currency} />}
                caption={
                  counted.length === rows.length
                    ? `${period.label} · ${rows.length} businesses`
                    : `${period.label} · ${counted.length} of ${rows.length} businesses`
                }
              />
              <Tile
                label="Money out, all businesses"
                value={<Money value={data.totals.expense} currency={currency} />}
                caption={period.label}
              />
              <Tile
                label="Profit, all businesses"
                value={<Signed value={data.totals.profit} currency={currency} />}
                caption={period.label}
              />
            </div>

            {/* The charts: their own query, their own failure, the same
                period and basis. The table below stands whether or not they
                load, and the table's numbers are the ones the tiles show. */}
            <OverviewCharts period={period} basis={basis} organisationId={organisation?.id} />
            {/* The spreadsheet view of the same months: every book added up,
                net of money between them, with each month's books one tap
                away. Same query as the charts, so one request serves both. */}
            <OverviewMonths period={period} basis={basis} organisationId={organisation?.id} />

            <Card>
              <CardHeader>
                <CardTitle>
                  <LayoutGrid className="size-4 text-muted-foreground" aria-hidden />
                  Profit by business
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ProfitBars rows={counted} currency={currency} withheld={rows.length - counted.length} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Business by business · {period.label}</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {/* Named, because the charts above carry visually-hidden
                    tables of the same books, and a test asking for "the row
                    with this business in it" has to be able to say which. */}
                <Table data-testid="overview-table">
                  <THead>
                    <TR>
                      <TH>Business</TH>
                      <TH>GST</TH>
                      <TH numeric>Money in</TH>
                      <TH numeric>Money out</TH>
                      <TH numeric>Profit</TH>
                      <TH numeric>Receivable</TH>
                      <TH numeric>Overdue</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {rows.map((row) => (
                      <BusinessRow key={row.orgId} row={row} currency={currency} onOpen={() => switchTo(row.orgId)} />
                    ))}
                    <BetweenBooksRow data={data} currency={currency} />
                    <TotalRow data={data} counted={counted} currency={currency} />
                  </TBody>
                </Table>
                <Caveats data={data} counted={counted.length} />
              </CardContent>
            </Card>

            <p className="text-xs text-muted-foreground">
              Filing status, open queries and the review checks for each business are on{' '}
              <Link to="/clients" className="underline" data-tap-exempt="inline text link in a sentence">
                Clients
              </Link>
              .
            </p>
          </>
        ) : null}
      </div>
    </>
  );
}

/**
 * One business. The whole row is clickable for a mouse; the NAME is a button,
 * which is the keyboard's and the screen reader's way in - a `<tr onClick>`
 * alone is a row a keyboard cannot reach.
 */
function BusinessRow({
  row,
  currency,
  onOpen,
}: {
  row: OverviewOrgRow;
  currency: string;
  onOpen: () => void;
}) {
  const rowCurrency = row.currency ?? currency;
  const figured = hasFigures(row);
  const overdueMinor = figured ? parse(row.overdue, rowCurrency).minor : 0;

  return (
    <TR
      onClick={onOpen}
      className="cursor-pointer"
      data-org-id={row.orgId}
    >
      <TD>
        <button
          type="button"
          onClick={(event) => {
            // The row's own handler would fire a second time through bubbling
            // and switch twice - harmless, but two cache clears for one click.
            event.stopPropagation();
            onOpen();
          }}
          className="inline-flex items-center gap-2 font-medium text-primary hover:underline"
          // The whole row is the tap target - see the TR - and the button is
          // the keyboard's way to the same thing; the hit area is drawn
          // around it so a finger on the name lands too.
          data-tap-exempt="name inside a row that is itself the target"
        >
          {/* The same dot the switcher shows, so the row and the book are
              recognisably one thing. Absent from an older response, it is
              simply not drawn. */}
          {row.colour ? <ColourDot colour={row.colour} className="h-2 w-2" /> : null}
          {row.name}
        </button>
        {figured ? (
          <span className="block text-xs text-muted-foreground">
            {row.invoiceCount ?? 0} {row.invoiceCount === 1 ? 'invoice' : 'invoices'}
            {rowCurrency !== currency ? ` · in ${rowCurrency}, not added below` : ''}
          </span>
        ) : (
          <span className="block text-xs text-muted-foreground">Figures not shown — see note below</span>
        )}
      </TD>
      <TD>
        <GstChip regime={row.gstRegime} />
      </TD>
      {figured ? (
        <>
          <TD numeric><Money value={row.income} currency={rowCurrency} showSymbol={false} /></TD>
          <TD numeric><Money value={row.expense} currency={rowCurrency} showSymbol={false} /></TD>
          <TD numeric><Signed value={row.profit} currency={rowCurrency} /></TD>
          <TD numeric><Money value={row.receivables} currency={rowCurrency} showSymbol={false} /></TD>
          <TD numeric>
            <Money
              value={row.overdue}
              currency={rowCurrency}
              showSymbol={false}
              className={overdueMinor > 0 ? 'font-medium text-destructive' : undefined}
            />
          </TD>
        </>
      ) : (
        // Dashes, not zeros: a zero says "nothing happened", and what happened
        // here is that this person may not see it. The em dash has an
        // accessible name so a screen reader does not read five silences.
        Array.from({ length: 5 }).map((_, i) => (
          <TD key={i} numeric className="text-muted-foreground">
            <span aria-label="not shown">—</span>
          </TD>
        ))
      )}
    </TR>
  );
}

/**
 * The Total row, which has to visibly add up.
 *
 * Money in, out and profit are the API's own totals - the same numbers as
 * the tiles - so a reader running a finger down the column lands on the
 * figure the tile shows. Receivable and overdue are not in the response's
 * totals and are summed here over the rows the API would have summed, and
 * the row is marked when that is fewer than all of them.
 */
function TotalRow({
  data,
  counted,
  currency,
}: {
  data: OverviewResponse;
  counted: ReturnType<typeof rowsInTotal>;
  currency: string;
}) {
  const sum = (pick: (row: (typeof counted)[number]) => string): MoneyValue =>
    counted.reduce((total, row) => add(total, parse(pick(row), currency)), zero(currency));
  const partial = counted.length < data.orgs.length;

  return (
    <TR className="border-t-2 border-t-border bg-muted/40 font-medium" data-testid="overview-total">
      <TD>
        Total
        {partial ? (
          <span className="ml-1 text-xs font-normal text-muted-foreground">
            ({counted.length} of {data.orgs.length} — see note below)
          </span>
        ) : null}
      </TD>
      <TD />
      <TD numeric><Money value={data.totals.income} currency={currency} showSymbol={false} /></TD>
      <TD numeric><Money value={data.totals.expense} currency={currency} showSymbol={false} /></TD>
      <TD numeric><Signed value={data.totals.profit} currency={currency} /></TD>
      <TD numeric><Money money={sum((row) => row.receivables)} currency={currency} showSymbol={false} /></TD>
      <TD numeric><Money money={sum((row) => row.overdue)} currency={currency} showSymbol={false} /></TD>
    </TR>
  );
}

/**
 * The elimination, as a row - so the column visibly adds up.
 *
 * The rows above count a salary the company paid as its expense and, in the
 * personal book, as income. The Total below counts neither. Without this
 * line between them the reader adds the column, gets a different number
 * from the Total, and concludes the page cannot add. Shown only when money
 * moved; a row of zeros is a row to wonder about.
 */
function BetweenBooksRow({ data, currency }: { data: OverviewResponse; currency: string }) {
  const between = data.totals.betweenBooks;
  if (!between) return null;
  const income = parse(between.income, currency);
  const expense = parse(between.expense, currency);
  if (income.minor === 0 && expense.minor === 0) return null;
  return (
    <TR className="text-muted-foreground" data-testid="overview-between-books">
      <TD>
        Between your books
        <span className="ml-1 text-xs font-normal">(not counted in the total)</span>
      </TD>
      <TD />
      <TD numeric>
        &minus; <Money money={income} currency={currency} showSymbol={false} />
      </TD>
      <TD numeric>
        &minus; <Money money={expense} currency={currency} showSymbol={false} />
      </TD>
      <TD />
      <TD />
      <TD />
    </TR>
  );
}

/**
 * What the API had to leave out, under the Total row it affects.
 *
 * Not a toast: a toast is gone before the reader reaches the number it
 * qualifies. The note sits under the table, where the dashes above point.
 */
function Caveats({ data, counted }: { data: OverviewResponse; counted: number }) {
  if (data.caveats.length === 0) return null;
  return (
    <div className="mt-3 space-y-1 text-xs text-muted-foreground" role="note" aria-label="Notes on these figures">
      <p className="font-medium">
        {counted < data.orgs.length
          ? `The totals cover ${counted} of ${data.orgs.length} businesses.`
          : 'A note on these figures.'}
      </p>
      <ul className="list-disc space-y-0.5 pl-4">
        {data.caveats.map((caveat) => (
          <li key={caveat}>{caveat}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Profit per business as ranked bars - and not a pie. The question is "which
 * book is carrying the other", a comparison of lengths. Losses get a bar too,
 * scaled by magnitude with the value shown signed and the name saying "loss",
 * so the colour is the third carrier of the fact rather than the only one.
 */
function ProfitBars({
  rows,
  currency,
  withheld,
}: {
  rows: ReturnType<typeof rowsInTotal>;
  currency: string;
  withheld: number;
}) {
  const minors = rows.map((row) => parse(row.profit, currency).minor);
  const shares = magnitudeShares(minors);
  const bars = rows
    .map((row, i) => ({
      id: row.orgId,
      name: profitLabel(row.name, minors[i]),
      colour: profitColour(minors[i]),
      amount: format(parse(row.profit, currency)),
      share: shares[i],
      minor: minors[i],
    }))
    .sort((a, b) => b.minor - a.minor);

  return (
    <>
      <RankedBars
        rows={bars}
        renderValue={(amount) => <Signed value={amount} currency={currency} />}
        emptyLabel="No business has figures to compare in this period."
      />
      {withheld > 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">
          {withheld} {withheld === 1 ? 'business is' : 'businesses are'} not in this chart — see the notes under the table.
        </p>
      ) : null}
    </>
  );
}
